import { useEffect, useMemo, useRef, useState } from "react";
import { absUrl, sameOrigin, useSiteOrigin } from "../useSiteOrigin";
import { mapBanners } from "../banners";
import { useSettings } from "../useSettings";
import "./bottom-banner.css";

const DEFAULT_REOPEN_AFTER_MS = 30 * 60 * 1000;
const DISMISSED_UNTIL_KEY = "kbv_bottom_banner_dismissed_until";

function resolveReopenAfterMs(value) {
  const ms = Number(value);
  return Number.isFinite(ms) && ms > 0 ? ms : DEFAULT_REOPEN_AFTER_MS;
}

function readDismissedUntil() {
  try {
    const until = Number(localStorage.getItem(DISMISSED_UNTIL_KEY));
    return Number.isFinite(until) && until > 0 ? until : null;
  } catch {
    return null;
  }
}

function writeDismissedUntil(until) {
  try {
    localStorage.setItem(DISMISSED_UNTIL_KEY, String(until));
  } catch {}
}

function clearDismissedUntil() {
  try {
    localStorage.removeItem(DISMISSED_UNTIL_KEY);
  } catch {}
}

function Tile({ banner, origin }) {
  const href = banner.cta ? absUrl(origin, banner.cta) : "";
  const external = href ? !sameOrigin(origin, href) : false;
  const image = <img src={banner.imageSrc} alt={banner.alt} loading="lazy" decoding="async" />;
  if (!href) return <div className="kbv-bottom-banner__tile">{image}</div>;
  return (
    <a
      className="kbv-bottom-banner__tile"
      href={href}
      rel={external ? "nofollow noopener noreferrer" : "nofollow"}
      {...(external ? { target: "_blank" } : {})}
    >
      {image}
    </a>
  );
}

export default function BottomBanner() {
  const origin = useSiteOrigin();
  const { settings, ready } = useSettings();
  const [dismissed, setDismissed] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const timerRef = useRef(null);
  const reopenAfterMs = useMemo(
    () => resolveReopenAfterMs(settings?.bottom_reopen_after_ms),
    [settings?.bottom_reopen_after_ms]
  );
  const reopenRef = useRef(reopenAfterMs);
  reopenRef.current = reopenAfterMs;

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    const schedule = (delayMs) => {
      clearTimer();
      if (delayMs <= 0) {
        clearDismissedUntil();
        setDismissed(false);
        return;
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        clearDismissedUntil();
        setDismissed(false);
      }, delayMs);
    };
    const sync = () => {
      const until = readDismissedUntil();
      const now = Date.now();
      if (until && now < until) {
        setDismissed(true);
        schedule(until - now);
        return;
      }
      clearDismissedUntil();
      clearTimer();
      setDismissed(false);
    };
    sync();
    setHydrated(true);
    const onVisibility = () => {
      if (document.visibilityState === "visible") sync();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimer();
    };
  }, []);

  const banners = useMemo(() => mapBanners(settings?.banner).slice(0, 4), [settings]);

  const dismiss = () => {
    const until = Date.now() + reopenRef.current;
    writeDismissedUntil(until);
    setDismissed(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      clearDismissedUntil();
      setDismissed(false);
    }, reopenRef.current);
  };

  if (!ready || !hydrated || dismissed || !banners.length) return null;

  return (
    <div className="kbv-home kbv-bottom-banner" role="region" aria-label="Banner quảng cáo">
      <div className="kbv-bottom-banner__stack">
        <button type="button" className="kbv-bottom-banner__close" onClick={dismiss}>
          X [Đóng]
        </button>
        <div className="kbv-bottom-banner__grid-wrap">
          <div className="kbv-bottom-banner__grid">
            {banners.map((banner) => (
              <Tile key={banner.id} banner={banner} origin={origin} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
